/**
 * Klasse für einen geometrischen Punkt
 */
export class Point {
  z = 0;

  /**
   * Typ des Punktes
   *
   * Ermöglicht es beispielsweise, verschiedene Koordinatensysteme zu unterscheiden.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected readonly TYPE?: string;

  /**
   * Erzeugt einen neuen Punkt
   *
   * @param x x-Koordinate, default: 0
   * @param y y-Koordinate, default: x
   * @param zRound z-Koordinate oder <b>true</b>, wenn die x- und y-Koordinaten gerundet werden sollen
   */
  constructor(
    public x = 0,
    public y: number = x,
    zRound?: number | boolean,
  ) {
    if (typeof zRound === 'number') {
      this.z = zRound;
    } else if (zRound === true) {
      this._round();
    }
  }

  /**
   * Kopiert diesen Punkt und addiert danach den übergebenen Punkt auf diesen
   */
  add(p: Point): this {
    return this.clone()._add(p);
  }

  /**
   * Addiert den übergebenen Punkt auf diesen Punkt
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _add(p: Point): this {
    this.x += p.x;
    this.y += p.y;
    return this;
  }

  /**
   * Kopiert diesen Punkt und subtrahiert danach den übergebenen Punkt von diesem
   */
  subtract(p: Point): this {
    return this.clone()._subtract(p);
  }

  /**
   * Subtrahiert den übergebenen Punkt von diesem
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _subtract(p: Point): this {
    this.x -= p.x;
    this.y -= p.y;
    return this;
  }

  /**
   * Kopiert diesen Punkt und multipliziert die x- und y-Koordinaten mit dem übergebenen Faktor
   */
  multiplyBy(n: number): this {
    return this.clone()._multiplyBy(n);
  }

  /**
   * Multipliziert die x- und y-Koordinaten dieses Punktes mit dem übergebenen Faktor
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _multiplyBy(n: number): this {
    this.x *= n;
    this.y *= n;
    return this;
  }

  /**
   * Kopiert diesen Punkt und dividiert die x- und y-Koordinaten durch den übergebenen Divisor
   */
  divideBy(n: number): this {
    return this.clone()._divideBy(n);
  }

  /**
   * Dividiert die x- und y-Koordinaten dieses Punktes durch den übergebenen Divisor
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _divideBy(n: number): this {
    this.x /= n;
    this.y /= n;
    return this;
  }

  /**
   * Kopiert diesen Punkt und multipliziert die Koordinaten mit den jeweiligen Koordinaten des übergebenen Punktes
   */
  scaleBy(p: Point): this {
    return this.clone()._scaleBy(p);
  }

  /**
   * Multipliziert die Koordinaten dieses Punktes mit den jeweiligen Koordinaten des übergebenen Punktes
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _scaleBy(p: Point): this {
    this.x *= p.x;
    this.y *= p.y;
    return this;
  }

  /**
   * Kopiert diesen Punkt und dividiert die Koordinaten durch die jeweiligen Koordinaten des übergebenen Punktes
   */
  unscaleBy(p: Point): this {
    return this.clone()._unscaleBy(p);
  }

  /**
   * Dividiert die Koordinaten dieses Punktes mit den jeweiligen Koordinaten des übergebenen Punktes
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _unscaleBy(p: Point): this {
    this.x /= p.x;
    this.y /= p.y;
    return this;
  }

  /**
   * Kopiert diesen Punkt und rundet die Koordinaten auf die angegebene Genauigkeit
   * precision muss angegeben werden als 10^z (z \in Z, also z.B. 0.1, 10, 0.01, etc.)
   */
  round(precision = 1): this {
    return this.clone()._round(precision);
  }

  /**
   * Rundet die Koordinaten dieses Punktes auf die angegebene Genauigkeit
   * precision muss angegeben werden als 10^z (z \in Z, also z.B. 0.1, 10, 0.01, etc.)
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _round(precision = 1): this {
    this.x = this.roundValue(this.x, precision);
    this.y = this.roundValue(this.y, precision);
    return this;
  }

  private roundValue(value: number, precision: number): number {
    if (precision === 1) {
      return Math.round(value);
    }
    // toFixed() vermeidet Rundungsfehler bei Float-Zahlen im Gegenzug für eine schlechtere
    // Performance - da round() aber nur extrem selten mit einer anderen precision als 1 aufgerufen
    // wird, ist das akzeptabel
    const numberOfDigits = precision.toString().split('.')[1]?.length || 0;
    if (numberOfDigits >= 0) {
      return Number(value.toFixed(numberOfDigits));
    } else {
      return Math.round(value / precision) * precision;
    }
  }

  /**
   * Kopiert diesen Punkt und rundet die Koordinaten ab
   */
  floor(): this {
    return this.clone()._floor();
  }

  /**
   * Rundet die Koordinaten dieses Punktes ab
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _floor(): this {
    this.x = Math.floor(this.x);
    this.y = Math.floor(this.y);
    return this;
  }

  /**
   * Kopiert diesen Punkt und rundet die Koordinaten auf
   */

  ceil(): this {
    return this.clone()._ceil();
  }

  /**
   * Rundet die Koordinaten dieses Punktes auf
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _ceil(): this {
    this.x = Math.ceil(this.x);
    this.y = Math.ceil(this.y);
    return this;
  }

  /**
   * Gibt die Distanz zu dem übergebenen Punkt zurück
   */
  distanceTo(p: Point): number {
    const dX = p.x - this.x,
      dY = p.y - this.y;
    return Math.sqrt(dX * dX + dY * dY);
  }

  /**
   * Gibt die quadrierte Distanz zu dem übergebenen Punkt zurück
   */
  distanceSquaredTo(p: Point): number {
    const dX = p.x - this.x,
      dY = p.y - this.y;
    return dX * dX + dY * dY;
  }

  /**
   * Kopiert diesen Punkt
   *
   * Nutzt den Laufzeit-Konstruktor (`this.constructor`), damit Unterklassen eine Instanz
   * ihres eigenen Typs erhalten. Unterklassen mit abweichender Konstruktor-Signatur müssen
   * `clone()` überschreiben.
   *
   * Ref: https://github.com/microsoft/TypeScript/issues/3841
   */
  clone(): this {
    return new (
      this.constructor as new (x: number, y: number, z?: number) => this
    )(this.x, this.y, this.z);
  }

  /**
   * Gibt eine String-Repräsentation dieses Punktes zurück
   */
  toString(): string {
    return `(${this.x},${this.y})`;
  }

  /**
   * Vergleicht zwei Punkte
   */
  equals(p: Point): boolean {
    return (
      this.TYPE === p.TYPE && this.x === p.x && this.y === p.y && this.z === p.z
    );
  }
}
